import ApiService from "../services/api";
import { Failure, FailureFormData, FailureVideo } from "../models/failure";

interface PaginationResponse {
  content: Failure[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}

export const fetchFailures = async (params: { page?: number, limit?: number }): Promise<Failure[]> => {
  const apiService = new ApiService();
  
  try {
    const response = await apiService.get<PaginationResponse>(`/failures`, { params });
    if (response.status !== 200) throw new Error("Error fetching failures");
    
    return response.data.content?.map((failure: any) => ({
      id: failure.id,
      title: failure.title,
      description: failure.description,
      videoUrl: failure.videoUrl,
      createdAt: failure.createdAt,
      updatedAt: failure.updatedAt,
    })) || [];
  } catch (error) {
    console.error('Error fetching failures:', error);
    return [];
  }
};

export const fetchFailuresWithPagination = async (params: { page?: number, limit?: number }): Promise<PaginationResponse> => {
  const apiService = new ApiService();
  
  try {
    const response = await apiService.get<PaginationResponse>(`/failures`, { params });
    if (response.status !== 200) throw new Error("Error fetching failures");
    
    return {
      content: response.data.content?.map((failure: any) => ({
        id: failure.id,
        title: failure.title,
        description: failure.description,
        videoUrl: failure.videoUrl,
        createdAt: failure.createdAt,
        updatedAt: failure.updatedAt,
      })) || [],
      totalElements: response.data.totalElements,
      totalPages: response.data.totalPages,
      size: response.data.size,
      number: response.data.number,
      first: response.data.first,
      last: response.data.last,
      empty: response.data.empty,
    };
  } catch (error) {
    console.error('Error fetching failures with pagination:', error);
    return {
      content: [],
      totalElements: 0,
      totalPages: 0,
      size: 10,
      number: 0,
      first: true,
      last: true,
      empty: true,
    };
  }
};

export const fetchFailureById = async (id: number): Promise<Failure | null> => {
  const apiService = new ApiService();
  
  try {
    const response = await apiService.get<any>(`/failures/${id}`);
    if (response.status !== 200) throw new Error("Error fetching failure");
    
    return {
      id: response.data.id,
      title: response.data.title,
      description: response.data.description,
      videoUrl: response.data.videoUrl,
      createdAt: response.data.createdAt,
      updatedAt: response.data.updatedAt,
    };
  } catch (error) {
    console.error('Error fetching failure by id:', error);
    return null;
  }
};

export const createFailure = async (title: string, description: string, videoFile: File): Promise<Failure | null> => {
  const apiService = new ApiService();
  
  try {
    const formData = new FormData();
    formData.append('title', title);
    formData.append('description', description);
    formData.append('video', videoFile);
    
    const response = await apiService.post<any>('/failures', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    if (response.status !== 200 && response.status !== 201) throw new Error("Error creating failure");
    
    return {
      id: response.data.id,
      title: response.data.title,
      description: response.data.description,
      videoUrl: response.data.videoUrl,
      createdAt: response.data.createdAt,
      updatedAt: response.data.updatedAt,
    };
  } catch (error) {
    console.error('Error creating failure:', error);
    return null;
  }
};

export const updateFailure = async (id: number, title: string, description: string, videoFile?: File): Promise<Failure | null> => {
  const apiService = new ApiService();
  
  try {
    const formData = new FormData();
    formData.append('title', title);
    formData.append('description', description);
    
    if (videoFile) {
      formData.append('video', videoFile);
    }
    
    const response = await apiService.put<any>(`/failures/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    if (response.status !== 200) throw new Error("Error updating failure");
    
    return {
      id: response.data.id,
      title: response.data.title,
      description: response.data.description,
      videoUrl: response.data.videoUrl,
      createdAt: response.data.createdAt,
      updatedAt: response.data.updatedAt,
    };
  } catch (error) {
    console.error('Error updating failure:', error);
    return null;
  }
};

export const deleteFailure = async (id: number): Promise<boolean> => {
  const apiService = new ApiService();
  
  try {
    const response = await apiService.delete(`/failures/${id}`);
    return response.status === 200 || response.status === 204;
  } catch (error) {
    console.error('Error deleting failure:', error);
    return false;
  }
};

export const fetchFailureVideo = async (id: number): Promise<FailureVideo | null> => {
  const apiService = new ApiService();
  
  try {
    const response = await apiService.get<any>(`/failures/${id}/video`);
    if (response.status !== 200) throw new Error("Error fetching failure video");
    
    return {
      id: response.data.id || id,
      url: response.data.url,
      filename: response.data.filename,
      size: response.data.size,
      mimeType: response.data.mimeType,
    };
  } catch (error) {
    console.error('Error fetching failure video:', error);
    return null;
  }
};
